import React, { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Star, Shield, Navigation, MapPin, Users, AlertTriangle,
  HeartHandshake, Siren, Bell, User, Trophy, Clock, Zap,
  Car, Footprints, Search, Plus, Sparkles, ChevronRight,
  Heart, Eye, Award, TrendingUp, LogOut, Settings, Crown
} from "lucide-react";

// ==================== ANIMATION VARIANTS ====================
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

// ==================== GLASSMORPHISM COMPONENTS ====================
const GlassCard = ({ children, className = "", glow = false, color = null }) => (
  <div className={`
    bg-black/40 backdrop-blur-2xl border border-white/10 rounded-3xl
    ${glow ? `shadow-[0_0_40px_-10px_${color || 'rgba(255,51,102,0.3)'}]` : ''}
    ${className}
  `}>
    {children}
  </div>
);

// Feature cards data
const FEATURES = [
  {
    id: "crush",
    path: "/crush",
    title: "Crush",
    subtitle: "Croisements",
    description: "Découvrez qui vous avez croisé",
    icon: Heart,
    color: "#FF3366",
    bgGradient: "from-pink-500 to-rose-600"
  },
  {
    id: "gps",
    path: "/gps",
    title: "GPS",
    subtitle: "Navigation",
    description: "Itinéraires style Waze",
    icon: MapPin,
    color: "#3B82F6",
    bgGradient: "from-blue-500 to-blue-600"
  },
  {
    id: "urgences",
    path: "/urgences",
    title: "Urgences",
    subtitle: "Alertes SOS",
    description: "Signaler incidents",
    icon: Siren,
    color: "#EF4444",
    bgGradient: "from-red-500 to-red-600"
  },
  {
    id: "entraide",
    path: "/bonnes-actions",
    title: "Entraide",
    subtitle: "Bonnes Actions",
    description: "Aidez et devenez Protecteur",
    icon: HeartHandshake,
    color: "#22C55E",
    bgGradient: "from-green-500 to-emerald-500"
  },
  {
    id: "cercles",
    path: "/cercles",
    title: "Cercles",
    subtitle: "Trust Circles",
    description: "Voisinage, école, travail",
    icon: Users,
    color: "#8B5CF6",
    bgGradient: "from-violet-500 to-purple-600"
  },
  {
    id: "safecheck",
    path: "/safecheck",
    title: "Safe Check",
    subtitle: "Trajet sécurisé",
    description: "Partagez votre position",
    icon: Navigation,
    color: "#F59E0B",
    bgGradient: "from-amber-500 to-orange-500"
  }
];

// ==================== DASHBOARD PAGE ====================
const DashboardPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, notifRes] = await Promise.all([
          axios.get(`${API}/good-deeds/my-stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/notifications?limit=5`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data);
        setNotifications(notifRes.data || []);
      } catch (e) {
        console.log("Fetch error:", e);
      }
    };
    if (token) fetchData();
  }, [token]);

  const levelInfo = {
    citoyen: { name: "Citoyen", color: "#6B7280", next: "Gardien", required: 3 },
    gardien: { name: "Gardien", color: "#3B82F6", next: "Légende", required: 10 },
    legende: { name: "Légende", color: "#F59E0B", next: null, required: null }
  };

  const currentLevel = levelInfo[user?.level || "citoyen"];
  const progress = currentLevel.required 
    ? Math.min(100, ((user?.positive_ratings || 0) / currentLevel.required) * 100)
    : 100;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Ambient Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[150px]" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-2xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-header.png" alt="Logo" className="w-10 h-10" />
              <span className="text-xl font-bold hidden sm:block">RATE<span className="text-amber-400">⭐</span>SHARE</span>
            </Link>

            {/* User Info */}
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <Link to="/notifications" className="relative p-2 rounded-xl hover:bg-white/5 transition-colors">
                <Bell size={20} className="text-zinc-400" />
                {notifications.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-pink-500 rounded-full" />
                )}
              </Link>

              {/* Profile */}
              <Link to="/profile" className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/5 transition-colors">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-sm font-bold">
                  {user?.full_name?.[0] || "?"}
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium">{user?.full_name || "Utilisateur"}</p>
                  <p className="text-xs text-zinc-500">{currentLevel.name}</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="space-y-8"
        >
          {/* Welcome Section */}
          <motion.div variants={fadeInUp}>
            <GlassCard className="p-6 md:p-8" glow>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold mb-2">
                    Bonjour, <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500">{user?.full_name?.split(' ')[0] || "ami"}</span> 👋
                  </h1>
                  <p className="text-zinc-400">Bienvenue sur votre espace personnel RateShare</p>
                </div>

                {/* Level Progress */}
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="flex items-center gap-2 mb-1">
                      <Award size={16} className="text-amber-400" />
                      <span className="font-bold" style={{ color: currentLevel.color }}>{currentLevel.name}</span>
                    </div>
                    {currentLevel.next && (
                      <p className="text-xs text-zinc-500">{user?.positive_ratings || 0}/{currentLevel.required} vers {currentLevel.next}</p>
                    )}
                  </div>
                  <div className="w-24 h-24 relative">
                    <svg className="w-full h-full -rotate-90">
                      <circle cx="48" cy="48" r="40" fill="none" stroke="#27272a" strokeWidth="6" />
                      <circle 
                        cx="48" cy="48" r="40" fill="none" 
                        stroke={currentLevel.color} strokeWidth="6"
                        strokeDasharray={`${progress * 2.51} 251`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-bold">{Math.round(progress)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </GlassCard>
          </motion.div>

          {/* Stats Row */}
          <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Notes reçues", value: user?.total_ratings || 0, icon: Star, color: "#F59E0B" },
              { label: "Notes positives", value: user?.positive_ratings || 0, icon: TrendingUp, color: "#22C55E" },
              { label: "Bonnes actions", value: stats?.total_deeds || 0, icon: Heart, color: "#FF3366" },
              { label: "Points", value: stats?.total_points || 0, icon: Zap, color: "#8B5CF6" }
            ].map((stat, i) => (
              <GlassCard key={i} className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${stat.color}20` }}>
                    <stat.icon size={20} style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-zinc-500">{stat.label}</p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </motion.div>

          {/* Feature Cards Grid */}
          <motion.div variants={fadeInUp}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Sparkles size={20} className="text-pink-500" />
              Fonctionnalités
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {FEATURES.map((feature, i) => (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link to={feature.path}>
                    <GlassCard 
                      className="p-5 h-full hover:border-white/20 transition-all duration-300 group cursor-pointer"
                      glow={feature.id === "crush"}
                      color={feature.color}
                    >
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${feature.bgGradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <feature.icon size={24} className="text-white" />
                      </div>
                      <h3 className="font-bold text-lg mb-1">{feature.title}</h3>
                      <p className="text-xs text-zinc-500 mb-2">{feature.subtitle}</p>
                      <p className="text-sm text-zinc-400">{feature.description}</p>
                      <div className="mt-4 flex items-center gap-1 text-xs text-pink-400 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span>Accéder</span>
                        <ChevronRight size={14} />
                      </div>
                    </GlassCard>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div variants={fadeInUp}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Zap size={20} className="text-amber-400" />
              Actions rapides
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Nouvelle note", icon: Star, path: "/search", color: "#F59E0B" },
                { label: "Signaler", icon: AlertTriangle, path: "/urgences", color: "#EF4444" },
                { label: "Bonne action", icon: HeartHandshake, path: "/bonnes-actions", color: "#22C55E" },
                { label: "Safe Check", icon: Navigation, path: "/safecheck", color: "#3B82F6" }
              ].map((action, i) => (
                <Link key={i} to={action.path}>
                  <GlassCard className="p-4 hover:border-white/20 transition-all group cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                        style={{ backgroundColor: `${action.color}20` }}
                      >
                        <action.icon size={20} style={{ color: action.color }} />
                      </div>
                      <span className="font-medium text-sm">{action.label}</span>
                    </div>
                  </GlassCard>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Premium CTA */}
          {!user?.is_premium && (
            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6 border-pink-500/30 bg-gradient-to-r from-pink-500/10 to-purple-500/10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                      <Crown size={28} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Passez Premium</h3>
                      <p className="text-sm text-zinc-400">Débloquez toutes les fonctionnalités</p>
                    </div>
                  </div>
                  <Link 
                    to="/premium"
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 font-bold text-center hover:opacity-90 transition-opacity"
                  >
                    Découvrir
                  </Link>
                </div>
              </GlassCard>
            </motion.div>
          )}

          {/* Admin Link */}
          {user?.is_admin && (
            <motion.div variants={fadeInUp}>
              <Link to="/admin-dashboard">
                <GlassCard className="p-4 border-amber-500/30 hover:border-amber-500/50 transition-all">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <Shield size={20} className="text-amber-500" />
                      </div>
                      <div>
                        <p className="font-bold">Admin Dashboard</p>
                        <p className="text-xs text-zinc-500">Gérer l'application</p>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-zinc-500" />
                  </div>
                </GlassCard>
              </Link>
            </motion.div>
          )}
        </motion.div>
      </main>

      {/* Bottom Navigation (Mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-black/80 backdrop-blur-2xl border-t border-white/5 safe-area-bottom">
        <div className="flex items-center justify-around py-3">
          {[
            { icon: MapPin, label: "GPS", path: "/gps" },
            { icon: Heart, label: "Crush", path: "/crush", active: true },
            { icon: HeartHandshake, label: "Entraide", path: "/bonnes-actions" },
            { icon: User, label: "Profil", path: "/profile" }
          ].map((item, i) => (
            <Link 
              key={i} 
              to={item.path}
              className={`flex flex-col items-center gap-1 px-4 py-1 ${item.active ? 'text-pink-500' : 'text-zinc-500'}`}
            >
              <item.icon size={20} />
              <span className="text-[10px]">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default DashboardPage;
